/*
  OK FBI — Phase II: Investigator Card (dynamic layers over OWNER-uploaded artwork)

  The card is a 16:10 canvas. The uploaded artwork is applied as a CSS
  background-image (never an <img> element) so:
    ● no floating <img> layer sits above the card
    ● an invalid URL silently falls back to the gradient — the browser
      NEVER renders its broken-image icon.

  Every participant field (name / agent_id / rank / points / streak /
  avatar) is placed as an absolutely-positioned layer whose position is
  driven by the template's `layout.safe_zones` (percent-based).

  Badges live in a dedicated horizontal Badge Rail immediately below the
  display name — INSIDE the card boundary. It never overflows, never
  overlaps other fields, and never increases card height.

  Adding a new dynamic field in a future phase (e.g. subscription tier
  badge) requires ONLY adding a new safe-zone key + a small render block
  here — the card system does NOT need to be rebuilt.
*/
import React from "react";

const TIER_ACCENTS = {
  standard: { border: "border-neutral-700",     ring: "shadow-[0_0_30px_rgba(160,160,160,0.08)]",
              gradient: "from-neutral-900 via-neutral-950 to-neutral-900",
              chip: "bg-neutral-800/70 text-neutral-200 border-neutral-700" },
  streamer: { border: "border-emerald-800",     ring: "shadow-[0_0_40px_rgba(16,185,129,0.18)]",
              gradient: "from-emerald-950 via-neutral-950 to-neutral-950",
              chip: "bg-emerald-900/60 text-emerald-200 border-emerald-800" },
  vip:      { border: "border-yellow-700",      ring: "shadow-[0_0_40px_rgba(234,179,8,0.22)]",
              gradient: "from-yellow-950 via-neutral-950 to-neutral-900",
              chip: "bg-yellow-900/50 text-yellow-200 border-yellow-800" },
  special_3:{ border: "border-fuchsia-800",     ring: "shadow-[0_0_45px_rgba(217,70,239,0.28)]",
              gradient: "from-fuchsia-950 via-neutral-950 to-neutral-900",
              chip: "bg-fuchsia-900/60 text-fuchsia-200 border-fuchsia-800" },
  elite_2:  { border: "border-cyan-700",        ring: "shadow-[0_0_50px_rgba(6,182,212,0.32)]",
              gradient: "from-cyan-950 via-neutral-950 to-neutral-900",
              chip: "bg-cyan-900/60 text-cyan-200 border-cyan-800" },
  royal_1:  { border: "border-amber-500",       ring: "shadow-[0_0_70px_rgba(245,158,11,0.5)]",
              gradient: "from-amber-950 via-yellow-900/40 to-neutral-950",
              chip: "bg-amber-900/70 text-amber-100 border-amber-600" },
};

const CATEGORY_LABEL = {
  standard: "STANDARD",
  streamer: "STREAMER",
  vip: "VIP",
  special_3: "SPECIAL · 3",
  elite_2: "ELITE · 2",
  royal_1: "ROYAL · 1",
};

/**
 * Positions a child inside a safe-zone rectangle expressed in percentages.
 * `zone = {x, y, w, h, align}` — all values 0..100.
 */
function Zone({ zone, children, className = "" }) {
  if (!zone) return null;
  const style = {
    left: `${zone.x}%`,
    top: `${zone.y}%`,
    width: `${zone.w}%`,
    height: `${zone.h}%`,
    display: "flex",
    alignItems: "center",
    justifyContent: zone.align === "end" ? "flex-end"
                    : zone.align === "center" ? "center" : "flex-start",
  };
  return (
    <div className={`absolute ${className}`} style={style}>
      {children}
    </div>
  );
}


/**
 * BadgeRail — horizontal, cumulative list of badges rendered INSIDE the
 * card. Never wraps, never increases card height, never overflows the
 * safe zone rectangle. Data-driven: each entry can be a plain string
 * (Phase II labels) or `{ label, icon_url, id }` (Phase III artwork).
 *
 * Overflow behaviour: shows at most `maxVisible` items and, if more
 * exist, appends a compact "+N" chip. The full collection lives on the
 * investigator profile page — this rail is intentionally compact.
 */
export function BadgeRail({ badges, zone, accentChip = "", maxVisible = 6,
                             size = "md" }) {
  const list = Array.isArray(badges) ? badges : [];
  if (!zone || list.length === 0) {
    // Reserve the empty rail so animating a future badge attach never
    // causes a layout jump on the card. It stays inside the card and
    // occupies zero horizontal space.
    if (!zone) return null;
    return <div className="absolute" data-testid="badge-rail-empty"
                style={{ left:`${zone.x}%`, top:`${zone.y}%`,
                         width:`${zone.w}%`, height:`${zone.h}%` }}/>;
  }
  const visible = list.slice(0, maxVisible);
  const extra = list.length - visible.length;
  const gap = { xl: "gap-1.5", lg: "gap-1", md: "gap-1", sm: "gap-[3px]" }[size] || "gap-1";
  const chipSize = {
    xl: "text-[10px] px-1.5 py-0.5",
    lg: "text-[9px]  px-1.5 py-0.5",
    md: "text-[8px]  px-1   py-[1px]",
    sm: "text-[7px]  px-1   py-[1px]",
  }[size] || "text-[9px] px-1.5 py-0.5";
  const iconPx  = { xl: 16, lg: 14, md: 12, sm: 10 }[size] || 14;

  return (
    <div className="absolute overflow-hidden"
         data-testid="badge-rail"
         style={{ left:`${zone.x}%`, top:`${zone.y}%`,
                  width:`${zone.w}%`, height:`${zone.h}%`,
                  display: "flex", alignItems: "center",
                  justifyContent: "flex-start" }}>
      <div className={`flex items-center ${gap} min-w-0`}
           style={{ maxWidth: "100%" }}>
        {visible.map((b, i) => {
          const label = typeof b === "string" ? b : (b?.label || b?.id || "");
          const icon  = typeof b === "object" ? (b?.icon_url || null) : null;
          return (
            <span key={i}
                  data-testid={`badge-rail-item-${i}`}
                  className={`inline-flex items-center gap-1 font-cairo rounded border ${accentChip} ${chipSize} whitespace-nowrap`}>
              {icon && (
                <span className="inline-block rounded-sm bg-black/20"
                      style={{
                        width: iconPx, height: iconPx,
                        backgroundImage: `url("${icon}")`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}/>
              )}
              {label && <span className="truncate">{label}</span>}
            </span>
          );
        })}
        {extra > 0 && (
          <span data-testid="badge-rail-more"
                className={`inline-flex items-center font-cairo rounded border ${accentChip} ${chipSize} whitespace-nowrap`}>
            +{extra}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * InvestigatorCard — renders a single participant on a category template.
 *
 * Props:
 *   template  · { category, artwork_url, layout }
 *   participant · { display_name, agent_id, rank, points, tiktok_username,
 *                   badges?: [string], streak?: {months:int}, avatar_url? }
 *   size      · "sm" | "md" | "lg" (controls the physical width)
 */
// ── Element rendering helpers (Slice 1) ─────────────────────────
// Reads per-element config from `template.layout.elements[key]`. When
// the element is absent (legacy layouts), falls back to `safe_zones`.
function _el(tpl, key) {
  const els = tpl?.layout?.elements || {};
  const legacy = tpl?.layout?.safe_zones?.[key];
  const merged = { ...(legacy || {}), ...(els[key] || {}) };
  if (Object.keys(merged).length === 0) return null;
  // A legacy safe_zone-only entry has no `visible` field. Treat as visible.
  if (merged.visible === false) return null;
  return merged;
}

function _elStyle(el) {
  if (!el) return {};
  const s = {};
  if (el.color) s.color = el.color;
  if (el.font_size_px) s.fontSize = `${el.font_size_px}px`;
  if (el.font_family && el.font_family !== "inherit") s.fontFamily = el.font_family;
  if (el.opacity != null && el.opacity !== 1) s.opacity = el.opacity;
  if (el.z != null) s.zIndex = el.z;
  return s;
}

function ElZone({ el, className = "", children }) {
  if (!el) return null;
  const style = {
    left: `${el.x}%`, top: `${el.y}%`,
    width: `${el.w}%`, height: `${el.h}%`,
    display: "flex", alignItems: "center",
    justifyContent: el.align === "end" ? "flex-end"
                    : el.align === "center" ? "center" : "flex-start",
    zIndex: el.z ?? 1,
  };
  return <div className={`absolute ${className}`} style={style}>{children}</div>;
}

// ────────────────────────────────────────────────────────────────
export function InvestigatorCard({ template, participant, size = "md" }) {
  const tpl = template || {};
  const cat = tpl.category || "standard";
  const accent = TIER_ACCENTS[cat] || TIER_ACCENTS.standard;
  // Slice 1: when OWNER uploaded artwork = visual_mode "custom" → uploaded
  // artwork IS the card. Suppress the tier border / glow / gradient / vignette
  // / auto tier-chip. Only OWNER-enabled dynamic elements render on top.
  const isCustom = tpl.visual_mode === "custom" && !!tpl.artwork_url;

  // Keep the editor/profile card fluid on narrow screens while retaining
  // the established desktop maximums. Fixed widths here caused the Profile
  // page to overflow horizontally on mobile/tablet viewports.
  const widthClass = size === "sm" ? "w-full max-w-[320px]"
                     : size === "lg" ? "w-full max-w-[560px]"
                     : "w-full max-w-[420px]";
  const p = participant || {};

  const frameClass = isCustom
    ? "rounded-2xl border border-transparent"
    : `rounded-2xl border ${accent.border} ${accent.ring} bg-gradient-to-br ${accent.gradient}`;

  // Read all element configs
  const elName    = _el(tpl, "display_name");
  const elId      = _el(tpl, "agent_id");
  const elTikTok  = _el(tpl, "tiktok_username");
  const elRank    = _el(tpl, "rank");
  const elPoints  = _el(tpl, "points");
  const elTier    = _el(tpl, "tier_label");
  const elStreak  = _el(tpl, "streak");
  const elSub     = _el(tpl, "subscription");
  const elBadges  = _el(tpl, "badge_rail") || _el(tpl, "badges");
  const elAvatar  = _el(tpl, "avatar");

  return (
    <div
      data-testid={`investigator-card-${cat}`}
      data-visual-mode={isCustom ? "custom" : "default"}
      className={`relative overflow-hidden ${frameClass} ${widthClass}`}
      style={{ aspectRatio: "16 / 10" }}
    >
      {/* Artwork background — CSS, never an <img> */}
      {tpl.artwork_url ? (
        <div
          data-testid={`card-artwork-${cat}`}
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("${tpl.artwork_url}")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: isCustom ? 1 : 0.95,
          }}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-cairo text-neutral-500 tracking-widest select-none">
          لم تُرفع صورة · ارفعها من مركز المالك
        </div>
      )}

      {/* Default-mode-only decorative vignette + auto tier chip */}
      {!isCustom && (
        <>
          <div className="absolute inset-0 pointer-events-none"
               style={{ background: "radial-gradient(ellipse at bottom left, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0.05) 100%)" }}/>
        </>
      )}

      {/* Tier label — OWNER-toggleable via element visibility */}
      {elTier && (!isCustom || elTier.visible !== false) && (
        <ElZone el={elTier}>
          <div className={`text-[9px] tracking-[0.3em] px-2 py-0.5 rounded border ${accent.chip} font-mono`}
               style={_elStyle(elTier)}>
            {CATEGORY_LABEL[cat] || cat.toUpperCase()}
          </div>
        </ElZone>
      )}

      {/* Avatar */}
      {elAvatar && (
        <ElZone el={elAvatar}>
          {p.avatar_url ? (
            <div className="h-full aspect-square border border-yellow-500/40 shadow-lg overflow-hidden"
                 style={{
                   borderRadius: (elAvatar.shape === "square") ? 0
                                  : (elAvatar.shape === "rounded") ? "12%" : "9999px",
                   backgroundImage: `url("${p.avatar_url}")`,
                   backgroundSize: "cover", backgroundPosition: "center",
                 }}/>
          ) : (
            <div className="h-full aspect-square border border-yellow-500/30 bg-neutral-900/70 flex items-center justify-center text-yellow-500/70 font-cairo text-xs"
                 style={{
                   borderRadius: (elAvatar.shape === "square") ? 0
                                  : (elAvatar.shape === "rounded") ? "12%" : "9999px",
                 }}>
              OK
            </div>
          )}
        </ElZone>
      )}

      {elName && (
        <ElZone el={elName}>
          <div className="font-cairo font-black leading-none truncate"
               style={{
                 fontSize: elName.font_size_px ? `${elName.font_size_px}px` : "clamp(14px, 3.2vw, 32px)",
                 color: elName.color || "#f6e58a",
                 fontFamily: elName.font_family && elName.font_family !== "inherit" ? elName.font_family : undefined,
                 opacity: elName.opacity ?? 1,
                 textShadow: "0 2px 12px rgba(0,0,0,0.7)",
               }}>
            {p.display_name || "—"}
          </div>
        </ElZone>
      )}

      {elBadges && (
        <BadgeRail
          badges={p.badges}
          zone={{ x: elBadges.x, y: elBadges.y, w: elBadges.w, h: elBadges.h, align: elBadges.align }}
          accentChip={accent.chip}
          maxVisible={elBadges.max_visible ?? 6}/>
      )}

      {elId && (
        <ElZone el={elId}>
          <div className="font-mono truncate"
               style={{
                 fontSize: elId.font_size_px ? `${elId.font_size_px}px` : "clamp(10px, 1.6vw, 18px)",
                 color: elId.color || "#fde68a",
                 fontFamily: elId.font_family && elId.font_family !== "inherit" ? elId.font_family : undefined,
                 opacity: elId.opacity ?? 1,
                 letterSpacing: "0.15em", textShadow: "0 2px 6px rgba(0,0,0,0.6)",
               }}>
            #{p.agent_id || "----"}
          </div>
        </ElZone>
      )}

      {elTikTok && p.tiktok_username && (
        <ElZone el={elTikTok}>
          <div className="font-mono truncate" style={_elStyle(elTikTok)}>
            @{p.tiktok_username}
          </div>
        </ElZone>
      )}

      {elRank && (
        <ElZone el={elRank}>
          <div className="font-cairo uppercase truncate"
               style={{
                 fontSize: elRank.font_size_px ? `${elRank.font_size_px}px` : "clamp(10px, 1.3vw, 14px)",
                 color: elRank.color || "#d1d5db",
                 opacity: elRank.opacity ?? 1,
                 letterSpacing: "0.18em",
               }}>
            {p.rank || "rookie"}
          </div>
        </ElZone>
      )}

      {elPoints && (
        <ElZone el={elPoints}>
          <div className="font-cairo tabular-nums"
               style={{
                 fontSize: elPoints.font_size_px ? `${elPoints.font_size_px}px` : "clamp(11px, 1.6vw, 18px)",
                 color: elPoints.color || "#fde68a",
                 opacity: elPoints.opacity ?? 1,
               }}>
            <span className="opacity-70 text-[0.75em] ms-1">نقطة</span>
            {Number(p.points || 0).toLocaleString("en-US")}
          </div>
        </ElZone>
      )}

      {elStreak && p.streak?.months ? (
        <ElZone el={elStreak}>
          <div className={`text-[10px] font-cairo px-2 py-0.5 rounded border ${accent.chip}`} style={_elStyle(elStreak)}>
            {p.streak.months}× شهر مشترك
          </div>
        </ElZone>
      ) : null}

      {elSub && p.subscription_label ? (
        <ElZone el={elSub}>
          <div className="font-cairo text-[10px]" style={_elStyle(elSub)}>
            {p.subscription_label}
          </div>
        </ElZone>
      ) : null}
    </div>
  );
}

/**
 * BroadcastCardsGrid — Auto-arranges multiple cards without overlap for
 * an OBS-style presentation. Simple cinematic staggered fade-in.
 */
export function BroadcastCardsGrid({ participants = [], templatesByCategory = {}, size = "md" }) {
  return (
    <div className="relative w-full">
      <style>{`
        @keyframes okfbi-card-in {
          0%   { opacity: 0; transform: translateY(14px) scale(0.985); filter: blur(4px); }
          60%  { opacity: 1; }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        .okfbi-card-anim > * {
          animation: okfbi-card-in 620ms ease-out both;
        }
      `}</style>
      <div className="okfbi-card-anim grid gap-4"
           style={{
             gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
           }}>
        {participants.map((p, i) => {
          const tpl = templatesByCategory[p.card_category || "standard"] || {};
          return (
            <div key={p.id || i} style={{ animationDelay: `${i * 90}ms` }}>
              <InvestigatorCard template={tpl} participant={p} size={size}/>
            </div>
          );
        })}
      </div>
    </div>
  );
}
